import React, { useState, useEffect, useMemo } from 'react';
import { reviewsManagementService, Review, ListReviewsParams } from '../services/reviewsManagement';
import './ReviewsPage.css';
import { Table, TableColumn } from '../components/Table';

export const ReviewsPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [approvedFilter, setApprovedFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const [viewingReview, setViewingReview] = useState<string | null>(null);
  const itemsPerPage = 50;

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadReviews reads from state
  }, [currentPage, ratingFilter, approvedFilter, searchTerm]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListReviewsParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        rating: ratingFilter !== 'all' ? parseInt(ratingFilter) : undefined,
        approved: approvedFilter !== 'all' ? approvedFilter === 'true' : undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await reviewsManagementService.listReviews(params);
      setReviews(response.reviews);
      setTotalCount(response.count);
      setLastEvaluatedKey(response.lastEvaluatedKey);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load reviews');
      console.error('Error loading reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadReviews();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRatingFilter('all');
    setApprovedFilter('all');
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleExport = () => {
    const csvContent = [
      ['Review ID', 'Provider ID', 'Customer ID', 'Rating', 'Approved', 'Comment', 'Created'].join(','),
      ...reviews.map(r => [
        r.reviewId,
        r.providerId,
        r.customerId,
        r.rating,
        r.approved ? 'Yes' : 'No',
        r.comment || '',
        r.createdAt,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reviews-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: TableColumn<Review>[] = useMemo(() => [
    {
      header: 'Review ID',
      accessor: 'reviewId',
      render: (value) => <div className="review-id">{(value as string).substring(0, 8)}...</div>,
    },
    {
      header: 'Provider ID',
      accessor: 'providerId',
      render: (value) => <div className="secondary-text">{(value as string).substring(0, 8)}...</div>,
      hideOnTablet: true,
    },
    {
      header: 'Customer ID',
      accessor: 'customerId',
      render: (value) => <div className="secondary-text">{(value as string).substring(0, 8)}...</div>,
      hideOnTablet: true,
    },
    {
      header: 'Rating',
      accessor: 'rating',
      render: (value) => (
        <div className="rating-display">
          {'★'.repeat(value as number)}{'☆'.repeat(5 - (value as number))}
        </div>
      ),
    },
    {
      header: 'Approved',
      accessor: 'approved',
      render: (value) => (
        <span className={value ? 'approved-badge' : 'pending-badge'}>
          {value ? 'Approved' : 'Pending'}
        </span>
      ),
    },
    {
      header: 'Comment',
      accessor: 'comment',
      render: (value) => (
        <div className="comment-text">{(value as string)?.substring(0, 50) || 'No comment'}...</div>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => formatDate(value as string),
    },
    {
      header: 'Actions',
      accessor: 'reviewId',
      render: (value) => (
        <button
          onClick={() => setViewingReview(value as string)}
          className="view-button"
        >
          View
        </button>
      ),
    },
  ], []);

  return (
    <div className="reviews-page">
      <div className="page-header">
        <h1>Review Management</h1>
        <div className="header-actions">
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-label">Total:</span>
              <span className="stat-value">{totalCount}</span>
            </div>
          </div>
          <button onClick={handleExport} className="export-button">
            Export CSV
          </button>
        </div>
      </div>

      <div className="reviews-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by comment..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">Search</button>
        </form>

        <div className="advanced-filters">
          <select
            value={ratingFilter}
            onChange={(e) => {
              setRatingFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>

          <select
            value={approvedFilter}
            onChange={(e) => {
              setApprovedFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="true">Approved</option>
            <option value="false">Pending</option>
          </select>

          <button onClick={handleClearFilters} className="clear-filters-button">
            Clear Filters
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading reviews...</div>
      ) : (
        <>
          <Table
            columns={columns}
            data={reviews}
            getRowId={(row) => row.reviewId}
            emptyMessage="No reviews found"
            containerClassName="reviews-table-container"
            className="reviews-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || reviews.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {viewingReview && (
        <ReviewDetailModal
          reviewId={viewingReview}
          onClose={() => {
            setViewingReview(null);
            loadReviews();
          }}
        />
      )}
    </div>
  );
};

const ReviewDetailModal: React.FC<{ reviewId: string; onClose: () => void }> = ({ reviewId, onClose }) => {
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);
  const [moderating, setModerating] = useState(false);

  useEffect(() => {
    loadReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on reviewId change only
  }, [reviewId]);

  const loadReview = async () => {
    try {
      setLoading(true);
      const data = await reviewsManagementService.getReview(reviewId);
      setReview(data);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to load review');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (action: 'approve' | 'reject' | 'delete') => {
    if (action === 'delete' && !confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
      return;
    }

    try {
      setModerating(true);
      await reviewsManagementService.moderateReview(reviewId, action);
      if (action === 'delete') {
        alert('Review deleted successfully');
        onClose();
      } else {
        await loadReview();
        alert(`Review ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : `Failed to ${action} review`);
    } finally {
      setModerating(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Loading review...</div>
        </div>
      </div>
    );
  }

  if (!review) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content review-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Review Details</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="modal-body">
          <div className="detail-section">
            <label>Review ID:</label>
            <div>{review.reviewId}</div>
          </div>
          <div className="detail-section">
            <label>Provider ID:</label>
            <div>{review.providerId}</div>
          </div>
          <div className="detail-section">
            <label>Customer ID:</label>
            <div>{review.customerId}</div>
          </div>
          <div className="detail-section">
            <label>Booking ID:</label>
            <div>{review.bookingId}</div>
          </div>
          <div className="detail-section">
            <label>Rating:</label>
            <div>{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)} ({review.rating}/5)</div>
          </div>
          {review.comment && (
            <div className="detail-section">
              <label>Comment:</label>
              <div className="comment-text-full">{review.comment}</div>
            </div>
          )}
          <div className="detail-section">
            <label>Status:</label>
            <div>
              <span className={review.approved ? 'approved-badge' : 'pending-badge'}>
                {review.approved ? 'Approved' : 'Pending'}
              </span>
            </div>
          </div>
          <div className="detail-section">
            <label>Created:</label>
            <div>{new Date(review.createdAt).toLocaleString()}</div>
          </div>
          <div className="modal-actions">
            {!review.approved && (
              <button
                onClick={() => handleModerate('approve')}
                disabled={moderating}
                className="approve-button"
              >
                {moderating ? 'Processing...' : 'Approve'}
              </button>
            )}
            {review.approved && (
              <button
                onClick={() => handleModerate('reject')}
                disabled={moderating}
                className="reject-button"
              >
                {moderating ? 'Processing...' : 'Reject'}
              </button>
            )}
            <button
              onClick={() => handleModerate('delete')}
              disabled={moderating}
              className="delete-button"
            >
              {moderating ? 'Deleting...' : 'Delete'}
            </button>
            <button onClick={onClose} className="cancel-button">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};
